import React, { useState } from 'react';
import { Shield, Key, Smartphone, Loader2, CheckCircle2, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { usersApi } from '../services/creatorApi';
import { generateSalt, hashPassword, verifyPassword } from '../services/authService';
import { toast } from 'sonner';
export function AccountSettings() {
  const {
    currentUser,
    refreshCurrentUser
  } = useWorkspace();
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  // PIN change state
  const [pinCurrentPassword, setPinCurrentPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [isChangingPin, setIsChangingPin] = useState(false);
  var hasPassword = !!(currentUser.Password_Hash && currentUser.Password_Salt);
  var hasPin = !!(currentUser.Quick_PIN_Hash && currentUser.Quick_PIN_Salt);
  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) return;
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setIsChangingPassword(true);
    try {
      // 1. Verify current password
      if (!currentUser.Password_Hash || !currentUser.Password_Salt) {
        throw new Error('Current password not set properly');
      }
      const isValid = await verifyPassword(currentPassword, currentUser.Password_Hash, currentUser.Password_Salt);
      if (!isValid) {
        toast.error('Current password is incorrect');
        setIsChangingPassword(false);
        return;
      }
      // 2. Hash new password
      const newSalt = generateSalt();
      const newHash = await hashPassword(newPassword, newSalt);
      // 3. Update user record
      await usersApi.update(currentUser.ID, {
        Password_Hash: newHash,
        Password_Salt: newSalt
      });
      // 4. Refresh local user state so subsequent operations use new hash
      await refreshCurrentUser();
      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      console.error('Failed to change password:', err);
      toast.error('Failed to update password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };
  const handlePinChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pinCurrentPassword || !newPin || !confirmPin) return;
    if (newPin !== confirmPin) {
      toast.error('PINs do not match');
      return;
    }
    setIsChangingPin(true);
    try {
      // 1. Verify current password
      if (!currentUser.Password_Hash || !currentUser.Password_Salt) {
        throw new Error('Current password not set properly');
      }
      const isValid = await verifyPassword(pinCurrentPassword, currentUser.Password_Hash, currentUser.Password_Salt);
      if (!isValid) {
        toast.error('Current password is incorrect');
        setIsChangingPin(false);
        return;
      }
      // 2. Hash new PIN
      const newSalt = generateSalt();
      const newHash = await hashPassword(newPin, newSalt);
      // 3. Update user record
      await usersApi.update(currentUser.ID, {
        Quick_PIN_Hash: newHash,
        Quick_PIN_Salt: newSalt
      });
      // 4. Refresh local user state so status indicators update
      await refreshCurrentUser();
      toast.success('Quick PIN updated successfully');
      setPinCurrentPassword('');
      setNewPin('');
      setConfirmPin('');
    } catch (err) {
      console.error('Failed to change PIN:', err);
      toast.error('Failed to update Quick PIN. Please try again.');
    } finally {
      setIsChangingPin(false);
    }
  };
  const inputClass = 'block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm';
  return <div className="max-w-4xl mx-auto space-y-8" data-id="element-2157" data-mp="11e173y9jghx1">
      <div data-id="element-2158" data-mp="wic4tn64wmx1">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2159" data-mp="wilsh43pihzk">
          Account Settings
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-2160" data-mp="1b7qe6v9pn4cf">
          Manage your security preferences and authentication methods.
        </p>
      </div>

      {/* Account Info */}
      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl overflow-hidden" data-id="element-2161" data-mp="ip2w7oq0g5fk">
        <div className="px-4 py-5 sm:p-6" data-id="element-2162" data-mp="drkzcwp9pgnx">
          <div className="flex items-center gap-3 mb-5" data-id="element-2163" data-mp="dqjc7tuz93pu">
            <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center text-lg font-semibold text-gray-600" data-id="element-2164" data-mp="2pdv48nkcslo3">
              {(currentUser.User_Name || 'U').charAt(0).toUpperCase()}
            </div>
            <div data-id="element-2165" data-mp="2fhjsbyoodxu6">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2166" data-mp="2fdvx8v3l13dc">
                {currentUser.User_Name || 'Unknown User'}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-2167" data-mp="3844k3092cby9">{currentUser.Email}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2168" data-mp="3d3z9t0h3qvhq">
            <div className="bg-gray-50 rounded-lg p-3" data-id="element-2169" data-mp="3d1czwu6qo3wc">
              <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2170" data-mp="2z36dca78pqbz">
                Role
              </p>
              <p className="text-sm font-medium text-gray-900 mt-0.5" data-id="element-2171" data-mp="333q8x9c60lw0">
                {currentUser.Role || '—'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3" data-id="element-2172" data-mp="1rq4izlyheg9f">
              <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2173" data-mp="1rrphtnrtwld3">
                Password
              </p>
              <div className="flex items-center gap-1.5 mt-0.5" data-id="element-2174" data-mp="3uew3manqzayr">
                {hasPassword ? <>
                    <CheckCircle2 className="h-4 w-4 text-green-500" data-id="element-2175" data-mp="3kmqed8ovr7n9" />
                    <span className="text-sm font-medium text-green-700" data-id="element-2176" data-mp="3kkvr3nq4z3g0">
                      Set
                    </span>
                  </> : <>
                    <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-2177" data-mp="3bkh5sod69oci" />
                    <span className="text-sm font-medium text-amber-700" data-id="element-2178" data-mp="36mnwvnkhig8j">
                      Not set
                    </span>
                  </>}
              </div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3" data-id="element-2179" data-mp="2wque3mg69qib">
              <p className="text-xs font-medium text-gray-500 uppercase" data-id="element-2180" data-mp="zxj5ik0n5efo">
                Quick PIN
              </p>
              <div className="flex items-center gap-1.5 mt-0.5" data-id="element-2181" data-mp="uyziegab9wlq">
                {hasPin ? <>
                    <CheckCircle2 className="h-4 w-4 text-green-500" data-id="element-2182" data-mp="13efapta1tna2" />
                    <span className="text-sm font-medium text-green-700" data-id="element-2183" data-mp="17fqs5birr276">
                      Set
                    </span>
                  </> : <>
                    <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-2184" data-mp="1h6v402d5e6xs" />
                    <span className="text-sm font-medium text-amber-700" data-id="element-2185" data-mp="1h86cgfhjg2v8">
                      Not set
                    </span>
                  </>}
              </div>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-4" data-id="element-2186" data-mp="th0xe2jf9elz">
            Passwords and PINs are securely hashed and cannot be viewed. Use the
            forms below to update them.
          </p>
        </div>
      </div>

      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl overflow-hidden" data-id="element-2187" data-mp="24wpieod483k5">
        <div className="px-4 py-5 sm:p-6 border-b border-gray-200" data-id="element-2188" data-mp="29u7takap0sus">
          <div className="flex items-center gap-3 mb-4" data-id="element-2189" data-mp="2uz1ildaf69f7">
            <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-2190" data-mp="2uzuqihl474yx">
              <Key className="h-5 w-5 text-blue-600" data-id="element-2191" data-mp="2qyrbfxaw5cyr" />
            </div>
            <div data-id="element-2192" data-mp="34pe9dn8tuyts">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2193" data-mp="34pwel9dgwndu">
                Change Password
              </h2>
              <p className="text-sm text-gray-500" data-id="element-2194" data-mp="2zwlahg01ikpm">
                Update your primary account password.
              </p>
            </div>
          </div>

          <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md mt-6" data-id="element-2195" data-mp="39oh8kr24matu">
            <div data-id="element-2196" data-mp="3nmny9jwx94if">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2197" data-mp="3nn76g8wrsej9">
                Current Password
              </label>
              <div className="relative" data-id="element-2198" data-mp="1t9pp53mykdwo">
                <input type={showCurrent ? 'text' : 'password'} required className={inputClass} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} data-id="element-2199" data-mp="1taqzt702aydv" />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-2200" data-mp="8s6qnqtiv16h">
                  {showCurrent ? <EyeOff className="h-4 w-4" data-id="element-2201" data-mp="3h7gscxti24dq" /> : <Eye className="h-4 w-4" data-id="element-2202" data-mp="3h9jp00p5qvrf" />}
                </button>
              </div>
            </div>

            <div data-id="element-2203" data-mp="38721lixrnx2u">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2204" data-mp="387k6t52eplmw">
                New Password
              </label>
              <div className="relative" data-id="element-2205" data-mp="3c8vzjjgkumzb">
                <input type={showNew ? 'text' : 'password'} required minLength={8} className={inputClass} value={newPassword} onChange={e => setNewPassword(e.target.value)} data-id="element-2206" data-mp="16ffie3k496gx" />
                <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-2207" data-mp="6nx22w3hh3cc">
                  {showNew ? <EyeOff className="h-4 w-4" data-id="element-2208" data-mp="3t3rioo699i7t" /> : <Eye className="h-4 w-4" data-id="element-2209" data-mp="kozr9hprytrh" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1" data-id="element-2210" data-mp="pm9csma1mla1">
                Must be at least 8 characters long.
              </p>
            </div>

            <div data-id="element-2211" data-mp="foljm0s35ltz">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2212" data-mp="fo26qyhalnmt">
                Confirm New Password
              </label>
              <input type={showNew ? 'text' : 'password'} required minLength={8} className={inputClass} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} data-id="element-2213" data-mp="2wbo0dv5iyqh1" />
            </div>

            <div className="pt-2" data-id="element-2214" data-mp="2nvymr7votgtc">
              <button type="submit" disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword} className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50" data-id="element-2215" data-mp="32t7kcjbl3gti">
                {isChangingPassword ? <Loader2 className="h-4 w-4 animate-spin mr-2" data-id="element-2216" data-mp="2f2296hgl9cpj" /> : null}
                Update Password
              </button>
            </div>
          </form>
        </div>

        <div className="px-4 py-5 sm:p-6 bg-gray-50/50" data-id="element-2217" data-mp="1mderevv1t5gq">
          <div className="flex items-center gap-3 mb-4" data-id="element-2218" data-mp="2c2lj615xvedt">
            <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center" data-id="element-2219" data-mp="2g4ykmddnkq4s">
              <Smartphone className="h-5 w-5 text-purple-600" data-id="element-2220" data-mp="2kvovu78rfn7e" />
            </div>
            <div data-id="element-2221" data-mp="2ky17y3ru73cj">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-2222" data-mp="2pui7rv8ef65g">
                Quick PIN / Phrase
              </h2>
              <p className="text-sm text-gray-500" data-id="element-2223" data-mp="1t5akois4sozf">
                Set up a short PIN or phrase to quickly unlock the app after
                brief inactivity.
              </p>
            </div>
          </div>

          <form onSubmit={handlePinChange} className="space-y-4 max-w-md mt-6" data-id="element-2224" data-mp="2245pprjfvt15">
            <div data-id="element-2225" data-mp="274thwgjemfwy">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2226" data-mp="275bifpd3ngao">
                Current Password
              </label>
              <p className="text-xs text-gray-500 mb-2" data-id="element-2227" data-mp="3dmglsmz8ffma">
                Required to verify your identity before changing your PIN.
              </p>
              <input type="password" required className={inputClass} value={pinCurrentPassword} onChange={e => setPinCurrentPassword(e.target.value)} data-id="element-2228" data-mp="16wd8fjvspjkj" />
            </div>

            <div data-id="element-2229" data-mp="s77by3oce7gc">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2230" data-mp="s7qv1npqb1vy">
                New Quick PIN / Phrase
              </label>
              <div className="relative" data-id="element-2231" data-mp="o76yal92i0ac">
                <input type={showPin ? 'text' : 'password'} required className={inputClass} value={newPin} onChange={e => setNewPin(e.target.value)} data-id="element-2232" data-mp="j6iypb6rwb4i" />
                <button type="button" onClick={() => setShowPin(!showPin)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-2233" data-mp="99wf0pq6nq42">
                  {showPin ? <EyeOff className="h-4 w-4" data-id="element-2234" data-mp="2bmslat4xoj71" /> : <Eye className="h-4 w-4" data-id="element-2235" data-mp="2gm585dvt1q9m" />}
                </button>
              </div>
            </div>

            <div data-id="element-2236" data-mp="21pntodsbjviu">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2237" data-mp="21o30arhqqimk">
                Confirm Quick PIN
              </label>
              <input type={showPin ? 'text' : 'password'} required className={inputClass} value={confirmPin} onChange={e => setConfirmPin(e.target.value)} data-id="element-2238" data-mp="26pscwbkm34cr" />
            </div>

            <div className="pt-2" data-id="element-2239" data-mp="1izy3k3zi3xx9">
              <button type="submit" disabled={isChangingPin || !pinCurrentPassword || !newPin || !confirmPin} className="inline-flex items-center justify-center rounded-md border border-transparent bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50" data-id="element-2240" data-mp="1nxhfgx8q7ym1">
                {isChangingPin ? <Loader2 className="h-4 w-4 animate-spin mr-2" data-id="element-2241" data-mp="14bfsq9joyccj" /> : null}
                Set Quick PIN
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}